<form class="space-y-6" role="region" aria-label="Create Brand" [formGroup]="form">
  <!-- Header -->
  <div
    class="hero overflow-hidden rounded-3xl border border-base-200 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/10 shadow-md"
    role="banner">
    <div
      class="hero-content w-full max-w-none flex-col items-stretch gap-6 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between lg:p-8">
      <div class="min-w-0 space-y-3">
        <div class="badge badge-primary badge-outline gap-2 px-3 py-3 text-xs font-semibold shadow-sm">
          <span class="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"></span>
          Brand Studio
        </div>

        <div class="space-y-2">
          <h1 class="text-3xl font-black tracking-tight sm:text-4xl">
            Create Brand
          </h1>
          <p class="max-w-2xl text-base font-medium text-base-content/80">
            Add a new brand to Supidoo catalog
          </p>
        </div>

        <div class="flex items-center gap-3">
          <div class="radial-progress text-primary" [style.--value]="progressPercent()"
            style="--size:3.25rem; --thickness: 7px;">
            <span class="text-xs font-bold">{{ progressPercent() }}%</span>
          </div>
          <div>
            <div class="text-sm font-semibold">{{ currentStepDef().title }}</div>
            <div class="text-xs text-base-content/60">Step {{ currentStepIndex() + 1 }} of {{ steps.length }}</div>
          </div>
        </div>
      </div>

      <!-- Sticky actions -->
      <div class="sticky-actions flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
        <button class="btn btn-primary btn-outline gap-2" type="button" (click)="saveDraft()"
          [disabled]="savingDraft()">
          @if (savingDraft()) { <span class="loading loading-spinner loading-sm"></span> }
          <i class="fa-solid fa-floppy-disk"></i>
          Save Draft
        </button>
        <button class="btn btn-primary gap-2" type="button" (click)="publish()" [disabled]="publishing()">
          @if (publishing()) { <span class="loading loading-spinner loading-sm"></span> }
          <i class="fa-solid fa-rocket"></i>
          Publish Brand
        </button>
        <button class="btn gap-2" type="button" (click)="cancel()" [disabled]="publishing() || savingDraft()">
          <i class="fa-solid fa-xmark"></i>
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Stepper -->
  <div class="card border border-base-200 bg-base-100 shadow-sm">
    <div class="card-body gap-4">
      <div class="stepper">
        <ul class="steps steps-horizontal w-full min-w-[56rem]">
          @for (s of steps; track s.id) {
          <li [attr.aria-label]="s.title" class="step cursor-pointer" [class.step-primary]="isStepReached(s.id)"
            (click)="goToStep(s.id)" (keydown.enter)="goToStep(s.id)" (keydown.space)="goToStep(s.id)" tabindex="0"
            role="button">
            <span class="inline-flex items-center gap-2">
              <i [class]="s.icon"></i>
              <span class="font-semibold">{{ s.title }}</span>
            </span>
          </li>
          }
        </ul>
      </div>

      <div class="flex items-center justify-between">
        <button class="btn btn-sm" type="button" (click)="prevStep()" [disabled]="currentStep() === steps[0].id">
          <i class="fa-solid fa-arrow-left"></i>
          Back
        </button>
        <button class="btn btn-sm btn-primary" type="button" (click)="nextStep()"
          [disabled]="currentStep() === steps[steps.length-1].id">
          Next
          <i class="fa-solid fa-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- BASIC -->
  @if (currentStep() === 'basic') {
  <div class="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-5">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-black">Basic Information</h2>
            <p class="text-sm text-base-content/60">Core identity and catalog status.</p>
          </div>
          <span class="badge badge-outline">Required fields marked *</span>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="sm:col-span-2">
            <label class="label" for="brand-name">
              <span class="label-text font-semibold">Brand Name *</span>
            </label>
            <input id="brand-name" class="input input-bordered w-full" type="text" formControlName="name"
              placeholder="e.g. Amul" />
            @if (form.controls.name.touched && form.controls.name.invalid) {
            <p class="mt-1 text-xs text-error">
              @if (form.controls.name.errors?.['required']) { Brand name is required. }
              @else { Brand name must be at least 2 characters. }
            </p>
            }
          </div>

          <div class="sm:col-span-2">
            <label class="label" for="brand-slug">
              <span class="label-text font-semibold">Slug *</span>
            </label>
            <input id="brand-slug" class="input input-bordered w-full" type="text" formControlName="slug"
              placeholder="e.g. amul" />
            <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
              <p class="text-xs text-base-content/60">
                Live URL: <span class="font-semibold">{{ urlPreview() }}</span>
              </p>
              @if (form.controls.slug.touched && form.controls.slug.invalid) {
              <p class="text-xs text-error">
                Slug must contain only lowercase letters, numbers, and hyphens.
              </p>
              }
            </div>
          </div>

          <div class="sm:col-span-2">
            <label class="label" for="short-desc">
              <span class="label-text font-semibold">Short Description</span>
            </label>
            <input id="short-desc" class="input input-bordered w-full" type="text" formControlName="shortDescription"
              placeholder="One-liner for quick cards" />
          </div>

          <div>
            <label class="label" for="brand-type">
              <span class="label-text font-semibold">Brand Type</span>
            </label>
            <select id="brand-type" class="select select-bordered w-full" formControlName="brandType">
              @for (t of brandTypes; track t) { <option [value]="t">{{ t }}</option> }
            </select>
          </div>

          <div>
            <label class="label" for="brand-status">
              <span class="label-text font-semibold">Status</span>
            </label>
            <select id="brand-status" class="select select-bordered w-full" formControlName="status">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-4">
        <h3 class="font-black">Live Preview</h3>
        <div class="rounded-2xl border border-base-200 overflow-hidden">
          <div class="h-24 bg-gradient-to-br from-primary/30 via-base-200 to-secondary/10"></div>
          <div class="-mt-10 p-4">
            <div class="flex items-end justify-between gap-3">
              <div class="flex items-end gap-3">
                <div class="avatar">
                  <div class="w-16 rounded-2xl border border-base-200 bg-base-100">
                    @if (logoUrl()) {
                    <img [src]="logoUrl()" alt="Logo preview" class="object-cover" />
                    } @else {
                    <div class="w-16 h-16 flex items-center justify-center text-base-content/40">
                      <i class="fa-solid fa-image text-xl"></i>
                    </div>
                    }
                  </div>
                </div>
                <div class="min-w-0">
                  <div class="text-lg font-black truncate">
                    {{ form.controls.name.value || 'Brand name' }}
                  </div>
                  <div class="text-xs text-base-content/60 truncate">
                    {{ urlPreview() }}
                  </div>
                </div>
              </div>
              <span class="badge badge-outline">
                {{ (form.controls.status.value || 'draft') | titlecase }}
              </span>
            </div>
            <p class="mt-3 text-sm text-base-content/70 line-clamp-2">
              {{ form.controls.shortDescription.value || 'Short description will appear here.' }}
            </p>
          </div>
        </div>
        <div class="rounded-2xl border border-base-200 bg-base-200/30 p-4">
          <div class="flex items-center justify-between">
            <div class="text-xs uppercase tracking-wider text-base-content/50 font-semibold">Draft autosave</div>
            <span class="badge badge-success badge-outline">Enabled</span>
          </div>
          <p class="mt-2 text-xs text-base-content/60">
            Logo and basic info are saved as you work.
          </p>
        </div>
      </div>
    </div>
  </div>
  }

  <!-- MEDIA -->
  @if (currentStep() === 'media') {
  <div class="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-5">
        <div>
          <h2 class="text-lg font-black">Media</h2>
          <p class="text-sm text-base-content/60">Upload brand assets. Crop support placeholder included.</p>
        </div>

        <div class="grid gap-4 sm:grid-cols-1">
          <!-- Logo -->
          <div class="sm:col-span-1">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-bold">Logo</h3>
            </div>
            
            <app-image-uploader 
              type="BRAND_LOGO" 
              [multiple]="false"
              [existingImageUrl]="logoUrl()"
              (filesSelected)="onFilesValidResult('logo', $event)"
              (imageRemoved)="removeUpload('logo')">
            </app-image-uploader>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Card -->
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-4">
        <h3 class="font-black">Brand Preview</h3>
        <div class="rounded-2xl border border-base-200 overflow-hidden">
          <div class="h-24 bg-base-200 relative">
            <div class="w-full h-full bg-gradient-to-br from-primary/20 via-base-200 to-secondary/10"></div>
          </div>
          <div class="-mt-10 p-4">
            <div class="flex items-end justify-between gap-3">
              <div class="flex items-end gap-3">
                <div class="avatar">
                  <div class="w-16 rounded-2xl border border-base-200 bg-base-100">
                    @if (logoUrl()) {
                    <img [src]="logoUrl()" alt="Logo preview" class="object-cover" />
                    } @else {
                    <div class="w-16 h-16 flex items-center justify-center text-base-content/40">
                      <i class="fa-solid fa-image text-xl"></i>
                    </div>
                    }
                  </div>
                </div>
                <div class="min-w-0">
                  <div class="text-lg font-black truncate">{{ form.controls.name.value || 'Brand name' }}</div>
                  <div class="text-xs text-base-content/60 truncate">{{ urlPreview() }}</div>
                </div>
              </div>
              <span class="badge badge-outline">Preview</span>
            </div>
            <p class="mt-3 text-sm text-base-content/70 line-clamp-2">
              {{ form.controls.shortDescription.value || 'Short description will appear here.' }}
            </p>
          </div>
        </div>

        <div class="rounded-2xl border border-base-200 bg-base-200/30 p-4">
          <div class="text-xs font-semibold uppercase tracking-wider text-base-content/50">Upload status</div>
          <div class="mt-2 space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span>Logo</span>
              <span class="font-semibold">{{ logoUrl() ? 100 : 0 }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  }


  <!-- PUBLISH -->
  @if (currentStep() === 'publish') {
  <div class="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-6">
        <div>
          <h2 class="text-lg font-black">Publish Settings</h2>
          <p class="text-sm text-base-content/60">Theme and SEO preview.</p>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label class="label" for="theme-color"><span class="label-text font-semibold">Theme color</span></label>
            <input id="theme-color" class="input input-bordered w-full" type="color" formControlName="accentColor" />
          </div>

          <label class="label cursor-pointer justify-start gap-3 sm:col-span-2">
            <input type="checkbox" class="toggle toggle-primary" formControlName="customizationEnabled" />
            <span class="label-text font-semibold">Brand page customization</span>
          </label>

          <div class="sm:col-span-2">
            <label class="label" for="meta-title"><span class="label-text font-semibold">Meta title</span></label>
            <input id="meta-title" class="input input-bordered w-full" type="text" formControlName="metaTitle"
              placeholder="e.g. Buy Amul Products Online" />
          </div>

          <div class="sm:col-span-2">
            <label class="label" for="meta-description"><span class="label-text font-semibold">Meta
                description</span></label>
            <textarea id="meta-description" class="textarea textarea-bordered w-full min-h-28"
              formControlName="metaDescription" placeholder="Fresh dairy products"></textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-4">
        <h3 class="font-black">Publish Summary</h3>
        <div class="rounded-2xl border border-base-200 p-4 space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-sm text-base-content/60">Brand</span>
            <span class="font-bold">{{ form.controls.name.value || '—' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm text-base-content/60">URL</span>
            <span class="font-semibold text-xs truncate max-w-[12rem]">{{ urlPreview() }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm text-base-content/60">Status</span>
            <span class="badge badge-outline">{{ (form.controls.status.value || 'draft') | titlecase }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-sm text-base-content/60">Logo</span>
            <span class="font-semibold">{{ logoUrl() ? 'Ready' : 'Missing' }}</span>
          </div>
        </div>

        <div class="alert alert-info">
          <i class="fa-solid fa-circle-info"></i>
          <div class="text-sm">
            Preview-only: uploads are mocked here. Replace with Supabase Storage upload when backend is ready.
          </div>
        </div>
      </div>
    </div>
  </div>
  }
</form>
